import React, { useState } from 'react'
import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import ActivityManagementEditSystemLayoutForm, {
  ActivityManagementEditSystemLayoutFormType,
  formToRequest,
  initialForm
} from '../../../components/admin/activity/ActivityManagementEditSystemLayoutForm'
import LoadingAndErrorFrame from '../../../components/default/frames/LoadingAndErrorFrame'
import {
  useRedirectHandleBack,
  useGetDataByParams,
  useDialogHandleSubmit
} from '../../../utils/default/ComplexFlowHook'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import allRoute, { Path } from '../../../components/admin/route/route'
import useT from '../../../i18ns/admin/useT'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import { defaultEditorState, parseContent } from '@golden/tiptap-react'
import { ActivityType } from '@golden/gdk-admin'
import { useChecker } from '../../../utils/admin/AdminRouteHook'

const ActivityManagementLayoutSystemUpdatePage: React.FC = () => {
  const classes = useCommonStyles()
  const { t } = useT()
  const gdk = useGDK()
  const globalDialog = useGlobalDialog()
  const writable = useChecker()
  const pageFlow = usePageFlow(true, false)
  const [defaultForm, setDefaultForm] = useState<ActivityManagementEditSystemLayoutFormType>(initialForm())

  useGetDataByParams({
    path: Path.ACTIVITY_MANAGEMENT_LAYOUT_SYSTEM_UPDATE,
    gdkFunc: (param: { id: string }) => {
      const { id } = param
      // 系統活動 對應 type call api
      switch (id) {
        case ActivityType.NEW_MEMBER_BONUS:
          return gdk.activity.getActivityLayoutNewMemberBonus()
        case ActivityType.RECOMMEND_FRIEND:
          return gdk.activity.getActivityLayoutRecommendFriend()
        case ActivityType.RANK_BONUS:
          return gdk.activity.getActivityLayoutRankBonus()
        case ActivityType.DAILY_SIGN_IN_BONUS:
          return gdk.activity.getActivityLayoutDailySignInBonus()
        case ActivityType.HIGH_CASH_BACK:
          return gdk.activity.getActivityLayoutFeedbackBonus()
        case ActivityType.WEEKLY_BONUS_GOLD:
          return gdk.activity.getActivityLayoutWeeklyBonus()
        case ActivityType.NEW_YEAR_2023:
          return gdk.activity.getActivityLayoutNewYear2023()
        case ActivityType.DRAGON_BOAT_FESTIVAL_2023:
          return gdk.activity.getActivityLayoutDragonBoatFestival2023()
        case ActivityType.NEW_YEAR_2024:
          return gdk.activity.getActivityLayoutNewYear2024()
        case ActivityType.NEW_YEAR_2026:
          return gdk.activity.getActivityLayoutNewYear2026()
        case ActivityType.DEPOSIT_BONUS:
          return gdk.activity.getActivityLayoutDepositBonus()
        case ActivityType.UEFA_BUILDING_UP:
          return gdk.activity.getActivityLayoutUefaBuildingUp()
        case ActivityType.UEFA_STARS:
          return gdk.activity.getActivityLayoutUefaStars()
        case ActivityType.WEEKLY_BONUS_DRAW:
          return gdk.activity.getActivityLayoutWeeklyBonusDraw()
        case ActivityType.DAILY_BONUS_DRAW:
          return gdk.activity.getActivityLayoutDailyBonusDraw()
        case ActivityType.NEW_YEAR_2025_SNAKE_YEAR_GOOD_LUCK:
          return gdk.activity.getActivityLayoutNewYear2025()
        case ActivityType.INVITE_FRIEND:
          return gdk.activity.getActivityLayoutInviteFriend()
        default:
          throw Error('missed system activity api')
      }
    },
    gdkFuncDependencies: [gdk],
    onBeforeFetch: pageFlow.setLoadingStart,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onSuccess: async (entry) => {
      setDefaultForm({
        activity_id: entry.activity_id,
        type: entry.type,
        title: entry.title,
        startAt: entry.start_at,
        endAt: entry.end_at,
        isNoEndAt: entry.end_at === null,
        showStartAt: entry.show_start_at,
        showEndAt: entry.show_end_at,
        isNoShowEndAt: entry.show_end_at === null,
        content: {
          ...defaultEditorState,
          ...entry.content ? {
            content: (await parseContent(entry.content))
          } : {}
        },
        desktopHomeImage: entry.desktop_home_image_url ? { url: entry.desktop_home_image_url, id: entry.desktop_home_image_id } : null,
        mobileHomeImage: entry.mobile_home_image_url ? { url: entry.mobile_home_image_url, id: entry.mobile_home_image_id } : null,
        couponImage: entry.coupon_image_url ? { url: entry.coupon_image_url, id: entry.coupon_image_id } : null,
        agentLongImage: entry.agent_long_image_url ? { url: entry.agent_long_image_url, id: undefined } : null,
        isCarousel: entry.is_carousel,
        isGuest: entry.is_guest,
        isMy: entry.is_my,
        tagIds: entry.tags.map(item => item.id),
        tournaments: entry.tournaments
      })
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError
  })

  const [handleBack, handleDebouncedBack] = useRedirectHandleBack({ path: allRoute.activityManagement.encodePath({ search: {}, param: {} }) })

  const { handleSubmit } = useDialogHandleSubmit({
    formToRequest,
    globalDialog,
    dialogId: 'updateActivityLayout',
    gdkFunc: (payload) => {
      const { type } = payload
      // 系統活動 對應 type call api

      switch (type) {
        case ActivityType.NEW_MEMBER_BONUS:
          return gdk.activity.updateActivityLayoutNewMemberBonus({ ...payload })
        case ActivityType.RECOMMEND_FRIEND:
          return gdk.activity.updateActivityLayoutRecommendFriend({ ...payload })
        case ActivityType.RANK_BONUS:
          return gdk.activity.updateActivityLayoutRankBonus({ ...payload })
        case ActivityType.DAILY_SIGN_IN_BONUS:
          return gdk.activity.updateActivityLayoutDailySignInBonus({ ...payload })
        case ActivityType.HIGH_CASH_BACK:
          return gdk.activity.updateActivityLayoutFeedbackBonus({ ...payload })
        case ActivityType.WEEKLY_BONUS_GOLD:
          return gdk.activity.updateActivityLayoutWeeklyBonus({ ...payload })
        case ActivityType.NEW_YEAR_2023:
          return gdk.activity.updateActivityLayoutNewYear2023({ ...payload })
        case ActivityType.DRAGON_BOAT_FESTIVAL_2023:
          return gdk.activity.updateActivityLayoutDragonBoatFestival2023({ ...payload })
        case ActivityType.NEW_YEAR_2024:
          return gdk.activity.updateActivityLayoutNewYear2024({ ...payload })
        case ActivityType.DEPOSIT_BONUS:
          return gdk.activity.updateActivityLayoutDepositBonus({ ...payload })
        case ActivityType.UEFA_BUILDING_UP:
          return gdk.activity.updateActivityLayoutUefaBuildingUp({ ...payload })
        case ActivityType.UEFA_STARS:
          return gdk.activity.updateActivityLayoutUefaStars({ ...payload })
        case ActivityType.WEEKLY_BONUS_DRAW:
          return gdk.activity.updateActivityLayoutWeeklyBonusDraw({ ...payload })
        case ActivityType.DAILY_BONUS_DRAW:
          return gdk.activity.updateActivityLayoutDailyBonusDraw({ ...payload })
        case ActivityType.NEW_YEAR_2025_SNAKE_YEAR_GOOD_LUCK:
          return gdk.activity.updateActivityLayoutNewYear2025({ ...payload })
        case ActivityType.NEW_YEAR_2026:
          return gdk.activity.updateActivityLayoutNewYear2026({ ...payload })
        case ActivityType.INVITE_FRIEND:
          return gdk.activity.updateActivityLayoutInviteFriend({ ...payload })
        default:
          throw Error('missed system activity api')
      }
    },
    gdkFuncDependencies: [gdk],
    getChangeDialogConfig: () => createGlobalDialogConfig({
      showIcon: false,
      message: t('dialog.confirmUpdate')
    }),
    getSuccessDialogConfig: () => createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.updateSuccess')
    }),
    getFailDialogConfig: (_, error) => createGlobalDialogConfig({
      showIcon: true,
      variant: 'error',
      showCancel: false,
      message: error.message
    }),
    afterSuccessDialog: handleBack
  })

  return (
    <React.Fragment>
      <Box paddingY={5} paddingX={4}>
        <LoadingAndErrorFrame {...pageFlow.status}>
          <Paper>
            <Box padding={4}>
              <Typography className={classes.goldenWord} variant="h5">{writable ? t('page.updateActivity') : t('page.lookActivity')}</Typography>
              <Box paddingY={4}>
                <ActivityManagementEditSystemLayoutForm
                  defaultValue={defaultForm}
                  onSubmit={handleSubmit}
                  okText={t('common.confirmUpdate')}
                  onBack={handleDebouncedBack}
                />
              </Box>
            </Box>
          </Paper>
        </LoadingAndErrorFrame>
      </Box>
    </React.Fragment>
  )
}

export default React.memo(ActivityManagementLayoutSystemUpdatePage)
